import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import InfoPopover from "../info-popover";

/*
 * The KPI tile used across the dashboard and the insights page.
 *
 * Promoted out of `pages/dashboard/index.tsx`, where it was a private component,
 * when Insights needed the same tile. The house rule is to promote rather than
 * copy: two divergent copies of a tile is exactly how a design system stops
 * being one.
 *
 * `action` was added for Insights — the score tiles double as filters — and is
 * optional, so the dashboard's usage is unchanged.
 */

interface DashboardInfoCardProps {
  title: string;
  value: string;
  infoMessage?: string;
  /** Renders the tile as a button. Use for tiles that filter or navigate. */
  onClick?: () => void;
  /** Marks the tile as the active choice when it acts as a filter. */
  active?: boolean;
  /** Slot beneath the value — a badge, a caption. */
  children?: React.ReactNode;
}

export default function DashboardInfoCard({
  title,
  value,
  infoMessage,
  onClick,
  active = false,
  children,
}: DashboardInfoCardProps) {
  const interactive = typeof onClick === "function";

  return (
    <Card
      className={cn(
        "relative",
        interactive && "cursor-pointer transition-colors hover:bg-accent/50",
        active && "ring-2 ring-primary",
      )}
      onClick={onClick}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={
        interactive
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      aria-pressed={interactive ? active : undefined}
    >
      {infoMessage ? (
        <div className="absolute top-2 right-2 z-10">
          <InfoPopover message={infoMessage} />
        </div>
      ) : null}
      <CardContent className="space-y-1">
        <div className={cn("text-base font-medium", infoMessage && "pr-8")}>
          {title}
        </div>
        <div className="text-4xl font-semibold tracking-tight">{value}</div>
        {children}
      </CardContent>
    </Card>
  );
}
